import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { adminExpenseApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Input from '../../components/ui/Input.jsx';
import Button from '../../components/ui/Button.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import ExpenseStatusBadge from '../../components/expense/ExpenseStatusBadge.jsx';
import { Download, Loader2, Columns3, Check } from 'lucide-react';
import { downloadBlob } from '../../lib/expenseExport.js';
import { cn } from '../../lib/utils.js';

// Source of truth for every column the report can show, in the fixed
// display order. `key` matches the row field from buildMonthlyReport.
const ALL_COLUMNS = [
  { key: 'claimRef', label: 'Ref' },
  { key: 'requesterName', label: 'Employee' },
  { key: 'claimPurpose', label: 'Purpose' },
  { key: 'totalClaimedSAR', label: 'Total (SAR)' },
  { key: 'submittedAt', label: 'Submission Date' },
  { key: 'status', label: 'Status' },
  { key: 'requesterDepartment', label: 'Department' },
  { key: 'paidAt', label: 'Paid Date' },
];
const DEFAULT_VISIBLE = ['claimRef', 'requesterName', 'claimPurpose', 'totalClaimedSAR', 'submittedAt', 'status'];
const COLUMNS_STORAGE_KEY = 'prohr_monthly_report_columns';

function loadVisibleColumns() {
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) return DEFAULT_VISIBLE;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_VISIBLE;
    const valid = parsed.filter((k) => ALL_COLUMNS.some((c) => c.key === k));
    return valid.length > 0 ? valid : DEFAULT_VISIBLE;
  } catch {
    return DEFAULT_VISIBLE;
  }
}

// Default to the current month in YYYY-MM, matching what the backend
// defaults to when `month` is omitted.
function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const STATUS_LABEL = {
  draft: 'Draft',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  paid: 'Paid',
};

export default function MonthlyExpenseReportPage() {
  const toast = useToast();
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ count: 0, totalClaimedSAR: 0, byStatus: {} });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(loadVisibleColumns);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminExpenseApi.monthlyReport(month);
      setRows(res.rows || []);
      setTotals(res.totals || { count: 0, totalClaimedSAR: 0, byStatus: {} });
    } catch (err) {
      toast.error(`Failed to load monthly report: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  // Close the column picker on outside click, matching UserMultiSelect.
  useEffect(() => {
    function onDocClick(e) {
      if (columnsRef.current && !columnsRef.current.contains(e.target)) {
        setColumnsOpen(false);
      }
    }
    if (columnsOpen) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [columnsOpen]);

  function toggleColumn(key) {
    setVisibleColumns((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      if (next.length === 0) return prev; // always keep at least one column
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }

  const handleExport = async () => {
    setExporting(true);
    const tid = toast.loading('Building export…');
    try {
      const { blob, fileName } = await adminExpenseApi.exportMonthlyReport(month, visibleColumns);
      downloadBlob(blob, fileName);
      toast.success('Export ready — downloading', { id: tid });
    } catch (err) {
      toast.error(`Export failed: ${err.message}`, { id: tid });
    } finally {
      setExporting(false);
    }
  };

  const COLUMN_RENDERERS = {
    claimRef: {
      label: 'Ref',
      render: (r) => <span className="font-mono text-xs font-semibold text-profun-cyan">{r.claimRef || '—'}</span>,
    },
    requesterName: {
      label: 'Employee',
      render: (r) => (
        <div>
          <div className="font-medium">{r.requesterName || '—'}</div>
          <div className="text-xs text-muted-foreground">{r.requesterEmail}</div>
        </div>
      ),
    },
    claimPurpose: {
      label: 'Purpose',
      render: (r) => (
        <span className="block max-w-[220px] truncate" title={r.claimPurpose || ''}>
          {r.claimPurpose || '—'}
        </span>
      ),
    },
    totalClaimedSAR: {
      label: 'Total (SAR)',
      render: (r) => <span className="font-mono font-semibold">{(r.totalClaimedSAR || 0).toFixed(2)}</span>,
    },
    submittedAt: {
      label: 'Submission Date',
      render: (r) => {
        if (!r.submittedAt) return '—';
        return new Date(r.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      },
    },
    status: {
      label: 'Status',
      render: (r) => <ExpenseStatusBadge status={r.status} />,
    },
    requesterDepartment: {
      label: 'Department',
      render: (r) => r.requesterDepartment || '—',
    },
    paidAt: {
      label: 'Paid Date',
      render: (r) => {
        if (!r.paidAt) return '—';
        return new Date(r.paidAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      },
    },
  };

  const columns = useMemo(
    () =>
      ALL_COLUMNS.filter((c) => visibleColumns.includes(c.key)).map((c) => ({
        key: c.key,
        label: COLUMN_RENDERERS[c.key].label,
        render: COLUMN_RENDERERS[c.key].render,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleColumns]
  );

  const statusEntries = Object.entries(totals.byStatus || {});

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Monthly Expense Report</h1>
          <p className="text-muted-foreground">
            Expense reports submitted in the selected month, across every status.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <Input
            label="Month"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-[180px]"
          />
          <div className="relative" ref={columnsRef}>
            <Button variant="secondary" onClick={() => setColumnsOpen((o) => !o)}>
              <Columns3 className="h-4 w-4" />
              Columns
            </Button>
            {columnsOpen && (
              <div className="absolute right-0 top-full mt-1 z-30 w-56 rounded-lg border border-border bg-card shadow-lg">
                <div className="border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Visible Columns
                </div>
                <div className="max-h-[280px] overflow-y-auto py-1">
                  {ALL_COLUMNS.map((c) => {
                    const checked = visibleColumns.includes(c.key);
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => toggleColumn(c.key)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                      >
                        <div
                          className={cn(
                            'h-4 w-4 rounded border flex items-center justify-center shrink-0',
                            checked ? 'border-primary bg-primary text-white' : 'border-border'
                          )}
                        >
                          {checked && <Check className="h-3 w-3" />}
                        </div>
                        <span className="flex-1">{c.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <Button variant="secondary" onClick={handleExport} disabled={exporting || loading}>
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Reports</div>
            <div className="text-2xl font-bold">{totals.count || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Claimed (SAR)</div>
            <div className="text-2xl font-bold font-mono text-profun-cyan">
              {(totals.totalClaimedSAR || 0).toFixed(2)}
            </div>
          </CardContent>
        </Card>
        {statusEntries.slice(0, 2).map(([status, bucket]) => (
          <Card key={status}>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                {STATUS_LABEL[status] || status}
              </div>
              <div className="text-2xl font-bold">{bucket.count}</div>
              <div className="text-xs text-muted-foreground font-mono">{bucket.totalClaimedSAR.toFixed(2)} SAR</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {statusEntries.length > 2 && (
        <div className="flex flex-wrap gap-3 mb-6">
          {statusEntries.slice(2).map(([status, bucket]) => (
            <div
              key={status}
              className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm"
            >
              <ExpenseStatusBadge status={status} />
              <span className="text-muted-foreground">
                {bucket.count} &middot; {bucket.totalClaimedSAR.toFixed(2)} SAR
              </span>
            </div>
          ))}
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyMessage="No expense reports were submitted in this month."
        keyFn={(r) => r.reportId}
      />
    </div>
  );
}
